import type { ReactNode } from "react";

import {
  useTodayStore,
  useTodayUi,
  useVisibleTodayQueue,
} from "../../stores/today";
import type { TodayGroupKey } from "../../lib/types";
import type { TodayQueueItemVM, TodayQueueVM } from "../../lib/view-model";

const SIDEBAR_GROUP_ORDER: TodayGroupKey[] = [
  "new",
  "readiness_moved",
  "high_intent",
  "timing",
  "your_followups",
];

export function TodayGroupSidebar({
  variant,
}: {
  variant: "desktop" | "mobile";
}) {
  const queue = useVisibleTodayQueue();
  const ui = useTodayUi();
  const activeGroups = SIDEBAR_GROUP_ORDER.map((key) => {
    return (
      queue.groups.find((group) => group.key === key) ?? {
        key,
        count: 0,
        items: [],
      }
    );
  });

  if (variant === "mobile") {
    return (
      <div className="overflow-x-auto border-b border-border-soft bg-bg-warm px-3 py-2 md:hidden">
        <div className="flex w-max gap-2">
          {activeGroups.map((group) => (
            <GroupChip
              key={group.key}
              group={group}
              active={ui.selectedGroupKey === group.key}
              onClick={() => selectAndScroll(group.key)}
            />
          ))}
          {queue.snoozed.length > 0 ? (
            <SnoozedChip
              count={queue.snoozed.length}
              onClick={selectSnoozedAndScroll}
            />
          ) : null}
        </div>
      </div>
    );
  }

  const zeroed = ui.zeroedGroupKeys.map((key) => ({
    key,
    count: 0,
    items: [],
  }));
  return (
    <nav aria-label="Today groups" className="flex h-full flex-col px-3 py-5">
      <div className="mb-5 px-2">
        <div className="text-[11px] font-bold tracking-[0.2em] text-fg-muted uppercase">
          Today&apos;s queue
        </div>
        <div className="mt-1 flex items-baseline gap-2">
          <span className="text-[22px] font-bold tabular-nums text-fg">
            {queue.openCount}
          </span>
          <span className="text-[12px] font-semibold text-fg-muted">
            to clear
          </span>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-6">
        <QueueSection label="Active">
          {activeGroups.map((group) => (
            <GroupRow
              key={group.key}
              group={group}
              active={ui.selectedGroupKey === group.key}
              onClick={() => selectAndScroll(group.key)}
            />
          ))}
        </QueueSection>

        <QueueSection label="Later">
          <SnoozedRow
            count={queue.snoozed.length}
            meta={peopleSummary(queue.snoozed)}
            active={ui.selectedGroupKey === null}
            onClick={selectSnoozedAndScroll}
          />
        </QueueSection>

        {zeroed.length > 0 && (
          <QueueSection label="Cleared">
            <div className="grid gap-1">
              {zeroed.map((group) => (
                <ZeroedRow
                  key={group.key}
                  group={group}
                  active={ui.selectedGroupKey === group.key}
                  onClick={() => selectAndScroll(group.key)}
                />
              ))}
            </div>
          </QueueSection>
        )}
      </div>
    </nav>
  );
}

function QueueSection({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <section className="grid gap-1.5">
      <SectionLabel>{label}</SectionLabel>
      <div className="grid gap-1">{children}</div>
    </section>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return (
    <div className="px-2 text-[11px] font-bold tracking-[0.14em] text-fg-muted uppercase">
      {children}
    </div>
  );
}

function CountBadge({ count }: { count: number }) {
  return (
    <span
      className={[
        "inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold tabular-nums",
        count > 0 ? "bg-pro-soft text-pro-ink" : "bg-muted text-fg-muted",
      ].join(" ")}
    >
      {count}
    </span>
  );
}

function QueueRow({
  title,
  count,
  secondary,
  meta,
  active = false,
  muted = false,
  onClick,
}: {
  title: string;
  count: number;
  secondary: string;
  meta: string;
  active?: boolean;
  muted?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group relative rounded-lg px-2 py-2.5 text-left outline-none transition-[background-color,color] hover:bg-accent/45"
    >
      {active ? (
        <span className="absolute inset-y-2 left-0 w-[3px] rounded-full bg-pro-ledge" />
      ) : null}
      <div className="flex min-w-0 items-center gap-2">
        <span
          className={[
            "min-w-0 flex-1 truncate text-[13.5px] font-semibold",
            muted ? "text-fg-muted" : "text-fg",
          ].join(" ")}
        >
          {title}
        </span>
        <CountBadge count={count} />
      </div>
      <div className="mt-1 grid gap-0.5">
        <div className="truncate text-[11.5px] font-medium text-fg-muted">
          {secondary}
        </div>
        <div className="truncate text-[11.5px] text-fg-muted/75">{meta}</div>
      </div>
    </button>
  );
}

function GroupRow({
  group,
  active,
  onClick,
}: {
  group: TodayQueueVM["groups"][number];
  active: boolean;
  onClick: () => void;
}) {
  return (
    <QueueRow
      title={labelForGroup(group.key)}
      count={group.count}
      secondary={metadataForGroup(group)}
      meta={peopleSummary(group.items)}
      active={active}
      onClick={onClick}
    />
  );
}

function SnoozedRow({
  count,
  meta,
  active,
  onClick,
}: {
  count: number;
  meta: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <QueueRow
      title="Snoozed"
      count={count}
      secondary="Hidden until later"
      meta={meta}
      active={active}
      muted
      onClick={onClick}
    />
  );
}

function ZeroedRow({
  group,
  active,
  onClick,
}: {
  group: TodayQueueVM["groups"][number];
  active: boolean;
  onClick: () => void;
}) {
  return (
    <QueueRow
      title={labelForGroup(group.key)}
      count={group.count}
      secondary="Cleared for now"
      meta="No open items"
      active={active}
      muted
      onClick={onClick}
    />
  );
}

function SnoozedChip({
  count,
  onClick,
}: {
  count: number;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex h-[34px] items-center gap-2 rounded-full bg-card px-3 text-sm font-semibold text-fg-muted no-underline outline-none"
    >
      <span>Snoozed</span>
      <span className="tabular-nums text-pro">{count}</span>
    </button>
  );
}

function GroupChip({
  group,
  active,
  onClick,
}: {
  group: TodayQueueVM["groups"][number];
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "inline-flex h-[34px] items-center gap-2 rounded-full bg-card px-3 text-sm font-semibold outline-none underline-offset-4",
        active
          ? "text-pro-ink underline decoration-pro-ledge decoration-2"
          : "text-fg-muted",
      ].join(" ")}
    >
      <span>{labelForGroup(group.key)}</span>
      <span
        className={[
          "tabular-nums",
          group.count > 0 ? "text-pro" : "text-fg-muted",
        ].join(" ")}
      >
        {group.count}
      </span>
    </button>
  );
}

function labelForGroup(key: TodayQueueVM["groups"][number]["key"]) {
  switch (key) {
    case "new":
      return "New";
    case "readiness_moved":
      return "Readiness moved";
    case "high_intent":
      return "High intent";
    case "timing":
      return "Timing changes";
    case "your_followups":
      return "Your follow-ups";
    default:
      return key;
  }
}

function metadataForGroup(group: TodayQueueVM["groups"][number]): string {
  const first = group.items[0];
  if (!first) return "Nothing here yet";
  if (first.kind === "task") return first.dueLabel ?? "Manual follow-up";
  switch (group.key) {
    case "new":
      return "Invite accepted";
    case "readiness_moved":
      return "Buying power changed";
    case "high_intent":
      return "High-intent activity";
    case "timing":
      return "Timing changed";
    case "your_followups":
      return "Manual follow-up";
    default:
      return first.reason;
  }
}

function peopleSummary(items: TodayQueueItemVM[]): string {
  const first = items[0];
  if (!first) return "No buyers";
  if (first.kind === "task") {
    return first.contactLabel ?? "No buyer";
  }
  const extra = items.length - 1;
  return extra > 0 ? `${first.client.name} +${extra}` : first.client.name;
}

function selectAndScroll(key: TodayQueueVM["groups"][number]["key"]) {
  useTodayStore.getState().selectGroupKey(key);
  const target = document.getElementById(`today-group-${key}`);
  target?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function selectSnoozedAndScroll() {
  useTodayStore.getState().selectGroupKey(null);
  document
    .getElementById("today-group-snoozed")
    ?.scrollIntoView({ behavior: "smooth", block: "start" });
}
